/**
 * Classroom and friend-notebook rules that live in the notebook: the
 * contest week (the class goal resets with it), the notebook card friends
 * see, the weekly class goal prize and taping a classmate's monster in.
 * The server keeps the classes; nothing here is typed text.
 */
import { CLASS_LIMITS, HERO_CLASS_IDS, type ClassDTO, type ClassGoalDTO, type NotebookCardDTO, type SharedShelfDTO } from '@shared/protocol/messages';
import { RULES } from '../combat/rules';
import { validateMonster } from '../combat/budget';
import { COSMETICS } from '../data/cosmetics';
import { t } from '../../i18n';
import { monsterFromDTO } from './monsters';
import type { Monster, SaveData } from './model';
import { addMemory } from './progression';

/** Coins for every classmate who helped once the class goal is reached. */
export const CLASS_GOAL_COINS = 30;
/** Class weeks remembered for the prize (older ones can never come back). */
const KEEP_CLASS_WEEKS = 12;
/** Contest weeks run on Thai time (UTC+7, no daylight saving). */
const ROUND_OFFSET_MS = 7 * 3600 * 1000;

/** The contest week of a moment, e.g. "2026-W43" (contest.Round on the server). */
export function contestRound(now = new Date()): string {
  const local = new Date(now.getTime() + ROUND_OFFSET_MS);
  // ISO week: the week of the Thursday in this Monday-to-Sunday week.
  const day = (local.getUTCDay() + 6) % 7;
  const thursday = new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() - day + 3));
  const year = thursday.getUTCFullYear();
  const week = 1 + Math.floor((thursday.getTime() - Date.UTC(year, 0, 1)) / (7 * 86400000));
  return `${year}-W${String(week).padStart(2, '0')}`;
}

/** What friends see of my notebook: the active hero and what I collected (never names I typed). */
export function notebookCard(save: SaveData): NotebookCardDTO {
  const hero = save.heroes.find((h) => h.id === save.activeHeroId) ?? null;
  const known = new Set(COSMETICS.map((c) => c.id));
  const owned = save.cosmetics.owned.filter((id) => known.has(id));
  const stamps = owned.filter((id) => COSMETICS.find((c) => c.id === id)?.kind === 'stamp').length;
  const heroClass = hero && (HERO_CLASS_IDS as readonly string[]).includes(hero.classId) ? (hero.classId as NotebookCardDTO['heroClass']) : '';
  return {
    heroClass,
    heroLevel: heroClass && hero ? Math.max(1, Math.min(RULES.progression.maxLevel, hero.level)) : 0,
    stamps,
    cosmetics: owned.length,
  };
}

/** The card to send, or null when friends already have this one. */
export function cardToSend(save: SaveData): NotebookCardDTO | null {
  const card = notebookCard(save);
  return JSON.stringify(card) === save.community.cardSent ? null : card;
}

/** Remembers which class I am in (null = none); battles only count for the class goal while set. */
export function syncClass(save: SaveData, cls: ClassDTO | null): boolean {
  const id = cls?.id ?? null;
  if (save.community.classId === id) return false;
  save.community.classId = id;
  return true;
}

/** The class goal prize is ready: the goal is done, I helped, and I have not claimed it this week. */
export const classPrizeReady = (save: SaveData, goal: ClassGoalDTO): boolean =>
  goal.done && goal.myBattles > 0 && !save.community.classPrizes.includes(goal.round);

/** Gives the class goal prize once per week. Returns the coins (0 when not ready). */
export function claimClassPrize(save: SaveData, goal: ClassGoalDTO, className: string): number {
  if (!classPrizeReady(save, goal)) return 0;
  save.community.classPrizes.push(goal.round);
  if (save.community.classPrizes.length > KEEP_CLASS_WEEKS) save.community.classPrizes.splice(0, save.community.classPrizes.length - KEEP_CLASS_WEEKS);
  save.coins += CLASS_GOAL_COINS;
  addMemory(save, {
    kind: 'quest',
    title: t('{name} reached the class goal!', { name: className }),
    text: t('{n} battles together this week. +{coins} coins for helping.', { n: goal.progress, coins: CLASS_GOAL_COINS }),
    sticker: 'star',
  });
  return CLASS_GOAL_COINS;
}

/** Goal progress as a 0..1 fraction. */
export const goalFraction = (goal: ClassGoalDTO): number => (goal.target > 0 ? Math.min(1, goal.progress / goal.target) : 0);

/** Battles still missing for the goal. */
export const goalLeft = (goal: ClassGoalDTO): number => Math.max(0, goal.target - goal.progress);

/** The goal a class of n members gets (protocol/classroom.go). */
export const goalTarget = (members: number): number => Math.max(CLASS_LIMITS.goalMin, members * CLASS_LIMITS.goalPerMember);

/**
 * Tapes a classmate's or friend's shared monster into my notebook (once per
 * share code) and returns it, or null when it fails the budget check.
 */
export function tapeShared(save: SaveData, item: SharedShelfDTO, author: string): Monster | null {
  const have = save.monsters.find((m) => m.source === 'shared' && m.shareCode === item.code);
  if (have) return have;
  if (!validateMonster(item.monster).ok) return null;
  const m = monsterFromDTO(item.monster, 'shared', author, item.code);
  save.monsters.push(m);
  return m;
}
